import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { logsAPI } from '../../api';

export default function ActivityPage() {
  const { t } = useTranslation();
  const { presenceId } = useParams();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [statsRes, logsRes] = await Promise.all([
        logsAPI.getStatistics(presenceId, 30),
        logsAPI.query({
          presenceId: presenceId,
          logType: 'activity',
          limit: 20,
        }),
      ]);
      setStats(statsRes.statistics || statsRes);
      setLogs(logsRes.logs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [presenceId]);

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-cyan-400/80 mb-1">
          {t('presence.activitySafety')}
        </div>
        <p className="text-sm text-slate-300">
          {t('presence.activityOverview')}
        </p>
      </header>

      {loading && <div className="text-sm text-slate-400">{t('presence.loadingActivity')}</div>}

      {!loading && stats && (
        <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatCard label={t('presence.totalActivities')} value={stats.totalActivities || 0} />
          <StatCard label={t('presence.llmInteractions')} value={stats.totalLLMInteractions || 0} />
          <StatCard label={t('presence.auditEvents')} value={stats.totalAuditEvents || 0} />
        </section>
      )}

      {!loading && (
        <section className="border border-slate-800 rounded-2xl bg-slate-950/70 px-4 py-3">
          <div className="text-xs text-slate-400 mb-2">{t('presence.recentEvents')}</div>
          <div className="space-y-1 text-xs text-slate-200 max-h-80 overflow-y-auto">
            {logs.length === 0 && (
              <div className="text-slate-500 text-sm">{t('presence.noEventsYet')}</div>
            )}
            {logs.map((e, idx) => (
              <div
                key={e.id || idx}
                className="flex justify-between items-center border-b border-slate-800/60 last:border-0 py-1"
              >
                <div>
                  <div className="font-medium text-slate-100">{e.type || e.action || t('presence.event')}</div>
                  <div className="text-[11px] text-slate-500">
                    {e.timestamp ? new Date(e.timestamp).toLocaleString() : 'N/A'}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">
                  {e.category && (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-slate-800">
                      {e.category}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="border border-slate-800 rounded-2xl bg-slate-950/80 px-4 py-3">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="text-2xl font-semibold text-cyan-300 mt-1">{value ?? 0}</div>
    </div>
  );
}
